<script setup>
import { computed, nextTick } from 'vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCross from '@/components/icon/IconCross.vue'
import IconPlusTiny from '@/components/icon/IconPlusTiny.vue'
import { REPLACEMENTS_MAX, FIND_MAX, REPLACE_MAX, newReplacementRow } from './captionRecognition'

const rows = defineModel('rows', { type: Array, required: true })

const props = defineProps({
	// { [rowKey]: { from?, to? } } blocking errors (client and server).
	errors: { type: Object, default: () => ({}) },
	sectionError: { type: String, default: '' },
	disabled: { type: Boolean, default: false },
})

const EXAMPLE = '\\bCaster\\b'

const inputs = new Map()
const setRef = (key, field) => (el) => {
	const id = `${key}:${field}`
	if (el) inputs.set(id, el)
	else inputs.delete(id)
}

const isBlank = (row) => row.from === '' && row.to === ''
const count = computed(() => rows.value.filter((r) => !isBlank(r)).length)
const full = computed(() => count.value >= REPLACEMENTS_MAX)

async function focusRow(key, field = 'from') {
	await nextTick()
	const el = inputs.get(`${key}:${field}`)
	if (!el) return
	el.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
	el.focus()
}

function add(at = rows.value.length) {
	const trailing = rows.value[rows.value.length - 1]
	if (at === rows.value.length && trailing && isBlank(trailing)) {
		focusRow(trailing.key)
		return
	}
	if (full.value) return
	const row = newReplacementRow()
	const next = [...rows.value]
	next.splice(at, 0, row)
	rows.value = next
	focusRow(row.key)
}

function onEnter(index) {
	const row = rows.value[index]
	if (!row || !row.from) return
	const next = rows.value[index + 1]
	if (next && isBlank(next)) focusRow(next.key)
	else add(index + 1)
}

function remove(index) {
	const row = rows.value[index]
	if (!row) return
	const neighbour = rows.value[index + 1] || rows.value[index - 1]
	rows.value = rows.value.filter((r) => r.key !== row.key)
	if (neighbour) focusRow(neighbour.key)
}

const message = (key) => props.errors[key]?.from || props.errors[key]?.to || ''

defineExpose({ focusRow, add })
</script>

<template>
	<div class="flex flex-col gap-3" data-testid="replacements">
		<div class="flex flex-col gap-1">
			<div class="flex items-baseline gap-2">
				<h3 id="replacements-heading" class="m-0 text-sm font-semibold text-surface-text-2">Replacements</h3>
				<span class="text-[11px] tabular-nums" :class="count > REPLACEMENTS_MAX ? 'text-error-light' : 'text-surface-7'" data-testid="replacements-count">{{ count }} / {{ REPLACEMENTS_MAX }}</span>
			</div>
			<p class="m-0 text-xs text-surface-7">
				Fix words the captions keep getting wrong. Find is a regular expression, e.g. <code class="rounded bg-surface-3 px-1 py-0.5 font-mono text-[11px] text-surface-text-1" v-text="EXAMPLE" />. Leave Replace empty to delete the match.
			</p>
		</div>

		<div v-if="rows.length" role="table" aria-labelledby="replacements-heading" class="flex flex-col gap-2 sm:gap-1.5">
			<div role="row" class="hidden text-[11px] font-medium uppercase tracking-wide text-surface-7 sm:grid sm:grid-cols-[minmax(0,1fr)_1rem_minmax(0,1fr)_2rem] sm:gap-2">
				<span role="columnheader">Find</span>
				<span role="columnheader" aria-hidden="true" />
				<span role="columnheader">Replace with</span>
				<span role="columnheader"><span class="sr-only">Remove</span></span>
			</div>
			<div
				v-for="(row, index) in rows"
				:key="row.key"
				role="row"
				class="flex flex-col gap-2 rounded-lg bg-surface-3/40 p-3 sm:grid sm:grid-cols-[minmax(0,1fr)_1rem_minmax(0,1fr)_2rem] sm:items-start sm:gap-2 sm:rounded-none sm:bg-transparent sm:p-0"
				:data-invalid="message(row.key) ? 'true' : undefined"
				data-testid="replacement-row"
			>
				<div role="cell" class="flex min-w-0 flex-col gap-1">
					<label :for="`find-${row.key}`" class="m-0 text-[11px] font-medium text-surface-7 sm:sr-only">Find, rule {{ index + 1 }}</label>
					<input
						:id="`find-${row.key}`"
						:ref="setRef(row.key, 'from')"
						v-model="row.from"
						type="text"
						:maxlength="FIND_MAX + 50"
						class="h-8 w-full rounded border-0 bg-surface-3 px-3 font-mono text-base text-surface-text-2 placeholder:font-sans placeholder:text-tiny placeholder:text-surface-7 focus:bg-surface-4 focus:outline-none focus:ring-0 md:text-tiny"
						:class="errors[row.key]?.from ? 'ring-1 ring-error-light' : ''"
						placeholder="Find (regex)"
						:aria-invalid="errors[row.key]?.from ? 'true' : undefined"
						:aria-describedby="message(row.key) ? `repl-err-${row.key}` : undefined"
						:disabled="disabled"
						autocomplete="off"
						spellcheck="false"
						data-testid="find-input"
						@keydown.enter.prevent="focusRow(row.key, 'to')"
					>
				</div>
				<span class="hidden h-8 items-center justify-center text-surface-7 sm:flex" aria-hidden="true">→</span>
				<div role="cell" class="flex min-w-0 flex-col gap-1">
					<label :for="`replace-${row.key}`" class="m-0 text-[11px] font-medium text-surface-7 sm:sr-only">Replace with, rule {{ index + 1 }}</label>
					<input
						:id="`replace-${row.key}`"
						:ref="setRef(row.key, 'to')"
						v-model="row.to"
						type="text"
						:maxlength="REPLACE_MAX + 50"
						class="h-8 w-full rounded border-0 bg-surface-3 px-3 text-base text-surface-text-2 placeholder:text-tiny placeholder:text-surface-7 focus:bg-surface-4 focus:outline-none focus:ring-0 md:text-tiny"
						:class="errors[row.key]?.to ? 'ring-1 ring-error-light' : ''"
						placeholder="Leave empty to delete the match"
						:aria-invalid="errors[row.key]?.to ? 'true' : undefined"
						:aria-describedby="message(row.key) ? `repl-err-${row.key}` : undefined"
						:disabled="disabled"
						autocomplete="off"
						data-testid="replace-input"
						@keydown.enter.prevent="onEnter(index)"
					>
				</div>
				<div role="cell" class="flex justify-end sm:block">
					<button
						type="button"
						class="inline-flex h-8 items-center gap-1.5 rounded px-2 text-tiny text-surface-7 transition-colors hover:bg-surface-3 hover:text-white disabled:opacity-40 sm:w-8 sm:justify-center sm:px-0"
						:aria-label="`Remove replacement ${index + 1}`"
						:disabled="disabled"
						data-testid="remove-replacement"
						@click="remove(index)"
					>
						<icon-base width="10" height="10"><icon-cross /></icon-base>
						<span class="sm:hidden">Remove</span>
					</button>
				</div>
				<p v-if="message(row.key)" :id="`repl-err-${row.key}`" class="m-0 text-xs text-error-light sm:col-span-4" data-testid="replacement-message">
					{{ message(row.key) }}
				</p>
			</div>
		</div>

		<div>
			<button
				type="button"
				class="inline-flex h-8 items-center gap-1.5 rounded-full border border-dashed border-surface-5 px-3 text-tiny font-medium text-surface-text-1 transition-colors hover:border-surface-7 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
				:disabled="disabled || full"
				:title="full ? `Up to ${REPLACEMENTS_MAX} replacements` : undefined"
				data-testid="add-replacement"
				@click="add()"
			>
				<icon-base width="10" height="10" width-view-box="12" height-view-box="12"><icon-plus-tiny /></icon-base>
				Add replacement
			</button>
		</div>
		<p v-if="sectionError" class="m-0 text-xs text-error-light">{{ sectionError }}</p>
	</div>
</template>
